import {
	stringConstants,
	primaryTokenStatusConstants,
	getOIDCRedirectURI,
} from '../AuthConstants';
import { logConstants } from '../../utils/Constants';
import { Logger } from '../Logger';
import OIDC from './OIDC';
import authWebviewHandler from '../dsAuth/AuthWebViewHandler';
import StoreConfigManager from '../StoreConfigManager';
import { PrimaryTokenServiceBase } from '../PrimaryTokenServiceBase';
import { config } from '../AuthConfig';
import { StoreConfiguration } from '../../Common/TypeDefinitions';
import { TokenResponse, TokenError } from '@openid/appauth/built/token_response';
import { OIDCTokensResponse, OIDCTokensTableKey } from './TypeDefinitions';
import OIDCTokenManager, { OIDCTokensUpdateReason } from './OIDCTokenManager';
import {
	GRANT_TYPE_REFRESH_TOKEN,
	TokenRequest,
	GRANT_TYPE_AUTHORIZATION_CODE,
} from '@openid/appauth/built/token_request';
import OIDCTokenRequestHandler from './OIDCTokenRequestHandler';
import { OIDCAuthorizationRequestHandler } from './OIDCAuthorizationRequestHandler';
import { PrimaryTokenFetchFailure } from '../AuthErrors';
import { AuthChallenge } from '../dsAuth/data/AuthChallenge';
import {
	GenericRequest,
	AuthenticationMode,
	HTTPFetchResponse,
	PrimaryTokenStatus,
} from '../dsAuth/data/AuthTypes';
import DSAuth from '../dsAuth/DSAuth';
import AuthUtils from '../AuthUtils';
import AuthMan from '../AuthMan';
import ctxsNative from '../ctxsNative';
import {
	getSHA512Digest,
	subtleCryptoAvailable,
} from '../../utils/Encryption/Encryption';
import { PrimaryTokensUpdateReason } from '../TokenManager';

export default class PrimaryTokenService extends PrimaryTokenServiceBase {
	private static instance: PrimaryTokenService;
	static getInstance(): PrimaryTokenService {
		if (!PrimaryTokenService.instance) {
			PrimaryTokenService.instance = new PrimaryTokenService();
		}
		return PrimaryTokenService.instance;
	}

	async getPrimaryTokenForURL(
		response: HTTPFetchResponse,
		referenceUri: string,
		id: string,
		transactionId: string,
		allowSilentLogin: boolean
	): Promise<PrimaryTokenStatus> {
		try {
			const authChallenge = await this.getAuthChallengeFromResponse(
				response,
				id,
				transactionId,
				referenceUri
			);

			const storeConfig = AuthMan.getInstance().getStoreConfiguration();
			const endPoints = await OIDC.getInstance().fetchAndStoreOIDCEndPoints(
				referenceUri,
				storeConfig,
				id,
				transactionId
			);
			const clientId = StoreConfigManager.getInstance().getClientid(
				storeConfig[referenceUri].oidcConfiguration,
				config.platform
			);

			// get any existing oidc tokens from indexeddb
			const oidcTokens = await OIDCTokenManager.getInstance().retrieveOIDCTokens({
				authDomain: storeConfig[referenceUri].authDomain,
				referenceUri: referenceUri,
			});

			let refreshTokenFlowTriggered = false;
			let oidcTokensResponse: TokenResponse;
			//If refresh token exists, do the refresh flow
			if (oidcTokens && oidcTokens.refreshToken) {
				//refresh access token using refresh token
				/* eslint-disable @typescript-eslint/naming-convention */
				const tokenRequest = new TokenRequest({
					client_id: clientId,
					grant_type: GRANT_TYPE_REFRESH_TOKEN,
					refresh_token: oidcTokens.refreshToken,
					redirect_uri: getOIDCRedirectURI(config.platform, config.hostApp), //performTokenRequest should remove redirect_uri before making fetch
					extras: {
						ctx_auth_domain: storeConfig[referenceUri].authDomain,
						ctx_access_token: oidcTokens.accessToken,
					},
				});
				/* eslint-enable @typescript-eslint/naming-convention */

				const cachedRefreshTokenHash = subtleCryptoAvailable()
					? await getSHA512Digest(oidcTokens.refreshToken)
					: '';
				const cachedRefreshTokenHashFirstHalf = cachedRefreshTokenHash.substring(
					0,
					Math.floor(cachedRefreshTokenHash.length / 2)
				);
				Logger.getInstance().write(
					logConstants.INFO,
					`Using existing refresh token with hash: ${cachedRefreshTokenHashFirstHalf}`
				);

				try {
					oidcTokensResponse =
						await OIDCTokenRequestHandler.getInstance().performTokenRequest(
							endPoints.token_endpoint,
							tokenRequest,
							referenceUri,
							transactionId,
							id
						);
					refreshTokenFlowTriggered = true;
				} catch (e) {
					if (e instanceof TokenError) {
						Logger.getInstance().write(
							logConstants.ERROR,
							`${id}: TokenError - deleting oidc tokens corresponding to auth domain ${storeConfig[referenceUri].authDomain} and reference uri ${referenceUri}`
						);
						await OIDCTokenManager.getInstance().deleteOIDCTokens({
							authDomain: storeConfig[referenceUri].authDomain,
							referenceUri: referenceUri,
						});
					}

					//only in case of INVALID_GRANT we want to continue to do fresh OIDC flow
					if (!(e instanceof TokenError) || e.error !== stringConstants.INVALID_GRANT) {
						throw e;
					}
				}
			}
			if (!oidcTokensResponse) {
				if (allowSilentLogin) {
					Logger.getInstance().write(
						logConstants.ERROR,
						`${id}: error in refresh flow when allowSilentLogin is requested : reference uri ${referenceUri}`
					);
					return new PrimaryTokenStatus(
						primaryTokenStatusConstants.ERROR_SILENT_LOGIN_FAILURE,
						true
					);
				} else {
					const { startUrl, request } =
						await OIDC.getInstance().getAuthorizationWebviewURLAndRequest(
							referenceUri,
							endPoints,
							clientId,
							id
						);

					// openchildtab resolves with response or error ( success - clear queue; failure - fallback to dsauth )
					const webviewResponse = await authWebviewHandler.start(
						startUrl,
						referenceUri,
						id,
						stringConstants.OIDCWEBVIEW,
						transactionId
					);
					// process response and decide whether to get primary token
					if (webviewResponse === primaryTokenStatusConstants.ERROR_CANCEL_LOGIN)
						return new PrimaryTokenStatus(webviewResponse, true);

					const authorizationResponse =
						OIDCAuthorizationRequestHandler.getInstance().parseWebviewResponse(
							webviewResponse,
							request,
							id
						);
					//Generate TokenRequest object and make a request to fetch access and primary tokens

					/* eslint-disable @typescript-eslint/naming-convention */
					const tokenRequest = new TokenRequest({
						client_id: clientId,
						code: authorizationResponse.code,
						grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
						redirect_uri: getOIDCRedirectURI(config.platform, config.hostApp),
						extras: {
							ctx_auth_domain: storeConfig[referenceUri].authDomain,
							code_verifier: request.internal.code_verifier,
						},
					});
					/* eslint-enable @typescript-eslint/naming-convention */

					oidcTokensResponse =
						await OIDCTokenRequestHandler.getInstance().performTokenRequest(
							endPoints.token_endpoint,
							tokenRequest,
							referenceUri,
							transactionId,
							id
						);
				}
			}
			const refreshTokenHash =
				subtleCryptoAvailable() && !!oidcTokensResponse.refreshToken
					? await getSHA512Digest(oidcTokensResponse.refreshToken)
					: '';
			const refreshTokenHashFirstHalf = refreshTokenHash.substring(
				0,
				Math.floor(refreshTokenHash.length / 2)
			);
			Logger.getInstance().write(
				logConstants.INFO,
				`Saving refresh token with hash: ${refreshTokenHashFirstHalf}`
			);

			//Save the access and refresh Tokens in db
			const tokenUpdateReason: OIDCTokensUpdateReason = refreshTokenFlowTriggered
				? 'refresh'
				: 'login';
			//if cached oidctokens entry doesn't have scopes value then we will not assume the scope used.
			let oidcScopesUsed: string;
			if (refreshTokenFlowTriggered) {
				if (!!oidcTokens.scopes) {
					oidcScopesUsed = oidcTokens.scopes;
				}
			} else {
				oidcScopesUsed = config.compressedGroupsEnabled
					? stringConstants.OIDC_SCOPES_WITH_COMPRESSED_GROUPS
					: stringConstants.OIDC_SCOPES;
			}
			await this.saveTokenResponse(
				storeConfig,
				oidcTokensResponse,
				referenceUri,
				tokenUpdateReason,
				oidcScopesUsed
			);

			//fetch primary token using access token
			const primaryTokenStatus = await this.fetchPrimaryToken(
				authChallenge,
				storeConfig,
				oidcTokensResponse.accessToken,
				id,
				transactionId,
				referenceUri,
				refreshTokenFlowTriggered ? 'refresh' : 'login'
			);

			if (refreshTokenFlowTriggered) {
				// Inform native that login succeeded with refresh token
				ctxsNative.RefreshTokenFlowTriggered(referenceUri, id);
			}
			return primaryTokenStatus;
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				id + ': ' + JSON.stringify(e) + ': message :' + e.message
			);
			if (e instanceof PrimaryTokenFetchFailure) throw e;
			else if (
				e.error &&
				e.error.toLowerCase().includes('AuthDomain invalid'.toLowerCase())
			) {
				Logger.getInstance().write(
					logConstants.ERROR,
					`${id}: Error in tokenrequest response: Authdomain changed ; throwing exception`
				);
				return new PrimaryTokenStatus(
					primaryTokenStatusConstants.ERROR_AUTHDOMAIN_CHANGED,
					true
				);
			} else throw new PrimaryTokenFetchFailure(e);
		}
	}

	private async saveTokenResponse(
		storeConfig: StoreConfiguration,
		response: TokenResponse,
		referenceUri: string,
		reason: OIDCTokensUpdateReason,
		scopes: string
	): Promise<void> {
		const responseJson = response.toJson();
		const oidcTokens: OIDCTokensResponse = {
			accessToken: responseJson.access_token,
			tokenType: responseJson.token_type,
			expiresAt: Math.floor(Date.now() / 1000) + responseJson.expires_in,
			refreshToken: responseJson.refresh_token,
		};

		const tokenTableKey: OIDCTokensTableKey = {
			authDomain: storeConfig[referenceUri].authDomain,
			referenceUri: referenceUri,
		};

		await OIDCTokenManager.getInstance().saveOIDCTokens(
			tokenTableKey,
			oidcTokens,
			storeConfig[referenceUri].tokenServiceURL,
			scopes,
			reason
		);
	}

	private async fetchPrimaryToken(
		challenge: AuthChallenge,
		storeConfig: StoreConfiguration,
		accessToken: string,
		id: string,
		transactionId: string,
		referenceUri: string,
		reason: PrimaryTokensUpdateReason
	): Promise<PrimaryTokenStatus> {
		const primaryTokenRequest = this.prepareRequestForPrimaryToken(
			challenge,
			accessToken,
			storeConfig[referenceUri].tokenServiceURL,
			transactionId
		);
		const primaryTokenResponse = await this.fetchToken(
			primaryTokenRequest,
			referenceUri,
			id
		);

		return await this.validateAndStoreToken(
			primaryTokenResponse,
			referenceUri,
			id,
			reason
		);
	}

	private prepareRequestForPrimaryToken(
		challenge: AuthChallenge,
		accessToken: string,
		serviceUrl: string,
		transactionId: string
	): GenericRequest {
		const data = DSAuth.getInstance().prepareTokenRequest(
			challenge,
			config.primaryTokenLifetime,
			serviceUrl,
			transactionId
		);
		AuthUtils.getInstance().addTokenToRequestHeader(
			data.headers,
			accessToken,
			AuthenticationMode.OIDC
		);
		return {
			method: stringConstants.POST,
			url: serviceUrl,
			headers: data.headers,
			body: data.body,
		};
	}
}
